import { useEffect } from 'react';

import { useOidcInteractionStore, useSessionStore } from '@/stores';

import { registerWebPush } from './web-push-service';

/**
 * Web push wiring (FCM Web SDK + service worker). Registers a token once the user
 * is authenticated and tears down foreground listeners on sign-out / unmount.
 * No-ops silently in unsupported browsers or when the VAPID key is unset.
 *
 * Skips registration during an OIDC interaction bridge (shop-bridge / sso-login):
 * those flows flip the web session to authenticated only to immediately navigate
 * away, so their transient browser context is not a device worth a push token.
 */
export function usePushNotifications(): void {
  const isAuthenticated = useSessionStore(s => s.isAuthenticated);
  const isOidcInteraction = useOidcInteractionStore(s => s.active);

  useEffect(() => {
    if (!isAuthenticated || isOidcInteraction) return;

    let cleanup: (() => void) | null = null;
    let cancelled = false;

    registerWebPush().then(fn => {
      if (cancelled) fn?.();
      else cleanup = fn;
    });

    return () => {
      cancelled = true;
      cleanup?.();
    };
  }, [isAuthenticated, isOidcInteraction]);
}
